import React, { useRef } from 'react';
import { Button } from '@heroui/react';
import { FileText, Image as ImageIcon, Trash2, Upload } from 'lucide-react';
import { UPLOAD_POLICIES } from '../attachments.js';

/**
 * AttachmentList — the scans on one credential: open, remove, add.
 *
 * Items come from `toAttachmentList` (`attachments.js`). Links are presigned
 * Spaces URLs: opened as-is in a new tab, `noreferrer` so the signed URL never
 * leaks as a Referer. An image shows its thumbnail; an item without a `url`
 * (storage couldn't sign it) is listed but not clickable.
 *
 * `onDelete` / `onUpload` are optional — omit them for a read-only list. With
 * `multiple`, `onUpload` receives an array of files (legacy uploaded batches).
 * `policy` ('documents' | 'pdf') sets the picker filter and the hint.
 */
function AttachmentList({
	items,
	onDelete,
	onUpload,
	uploading = false,
	busy = false,
	emptyText = 'Nothing attached yet.',
	uploadLabel,
	multiple = false,
	policy = 'documents'
}) {
	const list = Array.isArray(items) ? items : [];
	const fileRef = useRef(null);
	const label = uploadLabel || (multiple ? 'Add images or files' : 'Add image or file');
	const rules = UPLOAD_POLICIES[policy] || UPLOAD_POLICIES.documents;

	return (
		<div className="flex flex-col gap-2">
			{list.length > 0 ? (
				<ul className="list-none p-0 m-0 flex flex-col gap-2">
					{list.map(item => {
						const Icon = item.kind === 'image' ? ImageIcon : FileText;
						const preview = item.thumbnailUrl || (item.kind === 'image' ? item.url : null);
						return (
							<li
								key={`${item.kind}-${item.id ?? item.name}`}
								className="flex items-center gap-2.5 rounded-lg bg-default-100 px-3 py-2"
							>
								{preview ? (
									<a
										href={item.url || preview}
										target="_blank"
										rel="noopener noreferrer"
										className="shrink-0"
										aria-label={`Open ${item.name}`}
									>
										<img
											src={preview}
											alt=""
											referrerPolicy="no-referrer"
											className="w-10 h-10 rounded-md object-cover bg-default-200"
										/>
									</a>
								) : (
									<Icon
										className="w-4 h-4 shrink-0 text-[hsl(var(--heroui-primary))]"
										aria-hidden="true"
									/>
								)}
								{item.url ? (
									<a
										href={item.url}
										target="_blank"
										rel="noopener noreferrer"
										className="text-[13px] text-[hsl(var(--heroui-primary))] truncate flex-1 min-w-0 no-underline hover:underline"
									>
										{item.name}
									</a>
								) : (
									<span className="text-[13px] text-default-500 truncate flex-1 min-w-0">
										{item.name} · not available
									</span>
								)}
								{onDelete && (
									<Button
										isIconOnly
										size="sm"
										variant="light"
										color="danger"
										aria-label={`Remove ${item.name}`}
										isDisabled={busy || uploading}
										onPress={() => onDelete(item)}
									>
										<Trash2 className="w-3.5 h-3.5" />
									</Button>
								)}
							</li>
						);
					})}
				</ul>
			) : (
				<p className="text-[12.5px] text-default-400 m-0">{emptyText}</p>
			)}

			{onUpload && (
				<>
					{/* A real Button, not a styled <label>: Bootstrap's unlayered
					    `label { display: inline-block }` beat `inline-flex` and put the
					    icon and the text on two rows. */}
					<Button
						variant="bordered"
						radius="sm"
						className="self-start h-10 border-default-300"
						isDisabled={busy}
						isLoading={uploading}
						startContent={uploading ? null : <Upload className="w-4 h-4 text-default-500" />}
						onPress={() => fileRef.current && fileRef.current.click()}
					>
						<span className="text-[13px] font-medium">{uploading ? 'Uploading…' : label}</span>
					</Button>
					<input
						ref={fileRef}
						type="file"
						multiple={multiple}
						accept={rules.accept}
						className="hidden"
						onChange={e => {
							const files = Array.from(e.target.files || []);
							e.target.value = '';
							if (!files.length) return;
							// `multiple`: the whole batch at once; otherwise the one file.
							onUpload(multiple ? files : files[0]);
						}}
					/>
					<p className="text-[12px] text-default-400 m-0">{rules.hint}</p>
				</>
			)}
		</div>
	);
}

export default AttachmentList;
